import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Notifications as NotificationsIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

interface Alert {
  id: number;
  customer_id: number;
  customer_name: string;
  title: string;
  message: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH';
  status: 'ACTIVE' | 'ACKNOWLEDGED' | 'RESOLVED' | 'ESCALATED';
  created_at: string;
  acknowledged_at?: string;
  resolved_at?: string;
  acknowledged_by?: string;
  resolved_by?: string;
  escalation_count: number;
  max_escalations: number;
  next_escalation_time?: string;
}

interface EscalationTimeoutAlert {
  alert_id: number;
  customer_id: number;
  status: string;
  escalation_count: number;
  next_escalation_time?: string;
  time_until_escalation_minutes?: number;
  is_past_timeout: boolean;
}

interface AcknowledgmentTracking {
  alert_id: number;
  status: string;
  acknowledged_by?: string;
  acknowledged_at?: string;
  resolved_by?: string;
  resolved_at?: string;
  escalation_count: number;
  max_escalations: number;
  time_metrics: {
    time_to_acknowledge_minutes?: number;
    time_to_resolve_minutes?: number;
  };
  escalation_logs: Array<{
    escalation_level: number;
    escalated_to: string;
    escalation_method: string;
    sent_at: string;
    acknowledged: boolean;
    acknowledged_at?: string;
    acknowledged_by?: string;
  }>;
}

interface EscalationStats {
  analysis_period_days: number;
  start_date: string;
  end_date: string;
  escalation_statistics: Array<{
    escalation_level: number;
    total_escalations: number;
    acknowledged_count: number;
    acknowledgment_rate: number;
    avg_response_time_minutes?: number;
  }>;
  timeout_statistics: {
    total_alerts: number;
    escalated_count: number;
    escalation_rate: number;
    avg_resolution_time_minutes?: number;
  };
}

interface CaregiverResponsePattern {
  caregiver_name: string;
  caregiver_phone?: string;
  caregiver_email?: string;
  priority: number;
  total_escalations: number;
  acknowledged_count: number;
  acknowledgment_rate: number;
  avg_response_time_minutes?: number;
  preferred_method?: string;
}

const AdvancedAlertsDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [timeoutAlerts, setTimeoutAlerts] = useState<EscalationTimeoutAlert[]>([]);
  const [escalationStats, setEscalationStats] = useState<EscalationStats | null>(null);
  const [caregiverPatterns, setCaregiverPatterns] = useState<CaregiverResponsePattern[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [acknowledgeDialog, setAcknowledgeDialog] = useState<{ open: boolean; alert: Alert | null }>({ open: false, alert: null });
  const [resolveDialog, setResolveDialog] = useState<{ open: boolean; alert: Alert | null }>({ open: false, alert: null });
  const [trackingDialog, setTrackingDialog] = useState<{ open: boolean; tracking: AcknowledgmentTracking | null }>({ open: false, tracking: null });
  
  // Form states
  const [acknowledgeForm, setAcknowledgeForm] = useState({
    acknowledged_by: '',
    acknowledgment_notes: '',
    estimated_resolution_time: ''
  });
  
  const [resolveForm, setResolveForm] = useState({
    resolved_by: '',
    resolution_notes: '',
    resolution_method: ''
  });

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        return;
      }

      // Load alerts
      const alertsResponse = await fetch('/api/v1/alerts/', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (alertsResponse.ok) {
        const alertsData = await alertsResponse.json();
        setAlerts(alertsData.alerts || []);
      }

      // Load timeout alerts
      const timeoutResponse = await fetch('/api/v1/advanced-alerts/escalation-timeouts', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (timeoutResponse.ok) {
        const timeoutData = await timeoutResponse.json();
        setTimeoutAlerts(timeoutData);
      }

      // Load escalation stats
      const statsResponse = await fetch('/api/v1/advanced-alerts/escalation-stats?days=7', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setEscalationStats(statsData);
      }

      // Load caregiver patterns
      const patternsResponse = await fetch('/api/v1/advanced-alerts/caregiver-response-patterns?days=30', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (patternsResponse.ok) {
        const patternsData = await patternsResponse.json();
        setCaregiverPatterns(patternsData);
      }

    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledgeAlert = async () => {
    if (!acknowledgeDialog.alert) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/advanced-alerts/acknowledge/${acknowledgeDialog.alert.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          acknowledged_by: acknowledgeForm.acknowledged_by,
          acknowledgment_notes: acknowledgeForm.acknowledgment_notes || undefined,
          estimated_resolution_time: acknowledgeForm.estimated_resolution_time ? parseInt(acknowledgeForm.estimated_resolution_time) : undefined
        })
      });

      if (response.ok) {
        setAcknowledgeDialog({ open: false, alert: null });
        setAcknowledgeForm({ acknowledged_by: '', acknowledgment_notes: '', estimated_resolution_time: '' });
        loadDashboardData();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to acknowledge alert');
      }
    } catch (err) {
      setError('Failed to acknowledge alert');
      console.error('Error acknowledging alert:', err);
    }
  };

  const handleResolveAlert = async () => {
    if (!resolveDialog.alert) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/advanced-alerts/resolve/${resolveDialog.alert.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          resolved_by: resolveForm.resolved_by,
          resolution_notes: resolveForm.resolution_notes || undefined,
          resolution_method: resolveForm.resolution_method || undefined
        })
      });

      if (response.ok) {
        setResolveDialog({ open: false, alert: null });
        setResolveForm({ resolved_by: '', resolution_notes: '', resolution_method: '' });
        loadDashboardData();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to resolve alert');
      }
    } catch (err) {
      setError('Failed to resolve alert');
      console.error('Error resolving alert:', err);
    }
  };

  const loadAcknowledgmentTracking = async (alertId: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/advanced-alerts/acknowledgment-tracking/${alertId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const trackingData = await response.json();
        setTrackingDialog({ open: true, tracking: trackingData });
      } else {
        setError('Failed to load acknowledgment tracking');
      }
    } catch (err) {
      setError('Failed to load acknowledgment tracking');
      console.error('Error loading tracking:', err);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'HIGH': return 'error';
      case 'MEDIUM': return 'warning';
      case 'LOW': return 'info';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'error';
      case 'ACKNOWLEDGED': return 'warning';
      case 'RESOLVED': return 'success';
      case 'ESCALATED': return 'error';
      default: return 'default';
    }
  };

  const formatTimeSince = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ${diffHours % 24}h`;
    if (diffHours > 0) return `${diffHours}h ${diffMins % 60}m`;
    return `${diffMins}m`;
  };

  const formatDateTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Advanced Alerts Dashboard
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={loadDashboardData}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Active Alerts" />
          <Tab label="Timeout Monitoring" />
          <Tab label="Escalation Statistics" />
          <Tab label="Caregiver Performance" />
        </Tabs>
      </Paper>

      {/* Active Alerts Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Active Alerts ({alerts.filter(a => a.status === 'ACTIVE' || a.status === 'ACKNOWLEDGED').length})
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Customer</TableCell>
                        <TableCell>Alert</TableCell>
                        <TableCell>Severity</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Escalation</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {alerts
                        .filter(alert => alert.status === 'ACTIVE' || alert.status === 'ACKNOWLEDGED')
                        .map((alert) => (
                          <TableRow key={alert.id}>
                            <TableCell>{alert.customer_name}</TableCell>
                            <TableCell>
                              <Typography variant="subtitle2">{alert.title}</Typography>
                              <Typography variant="body2" color="textSecondary">
                                {alert.message.substring(0, 100)}...
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={alert.severity}
                                color={getSeverityColor(alert.severity)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={alert.status}
                                color={getStatusColor(alert.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {alert.escalation_count}/{alert.max_escalations}
                              </Typography>
                              {alert.next_escalation_time && (
                                <Typography variant="caption" color="textSecondary">
                                  Next: {formatTimeSince(alert.next_escalation_time)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatTimeSince(alert.created_at)} ago
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {formatDateTime(alert.created_at)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" gap={1}>
                                <Tooltip title="View Tracking">
                                  <IconButton
                                    size="small"
                                    onClick={() => loadAcknowledgmentTracking(alert.id)}
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>
                                {alert.status === 'ACTIVE' && (
                                  <Tooltip title="Acknowledge">
                                    <IconButton
                                      size="small"
                                      onClick={() => setAcknowledgeDialog({ open: true, alert })}
                                    >
                                      <CheckCircleIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {alert.status === 'ACKNOWLEDGED' && (
                                  <Tooltip title="Resolve">
                                    <IconButton
                                      size="small"
                                      onClick={() => setResolveDialog({ open: true, alert })}
                                    >
                                      <CheckCircleIcon color="success" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Timeout Monitoring Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Escalation Timeout Monitoring ({timeoutAlerts.length})
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Alert ID</TableCell>
                        <TableCell>Customer ID</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Escalation Level</TableCell>
                        <TableCell>Time Until Escalation</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {timeoutAlerts.map((timeoutAlert) => (
                        <TableRow key={timeoutAlert.alert_id}>
                          <TableCell>{timeoutAlert.alert_id}</TableCell>
                          <TableCell>{timeoutAlert.customer_id}</TableCell>
                          <TableCell>
                            <Chip
                              label={timeoutAlert.status}
                              color={getStatusColor(timeoutAlert.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{timeoutAlert.escalation_count}</TableCell>
                          <TableCell>
                            {timeoutAlert.time_until_escalation_minutes !== undefined ? (
                              <Typography
                                variant="body2"
                                color={timeoutAlert.is_past_timeout ? 'error' : 'warning'}
                              >
                                {timeoutAlert.is_past_timeout ? 'PAST TIMEOUT' : `${Math.round(timeoutAlert.time_until_escalation_minutes)}m`}
                              </Typography>
                            ) : (
                              'N/A'
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={timeoutAlert.is_past_timeout ? 'URGENT' : 'WARNING'}
                              color={timeoutAlert.is_past_timeout ? 'error' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Escalation Statistics Tab */}
      {activeTab === 2 && escalationStats && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Escalation Success Rates
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={escalationStats.escalation_statistics}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="escalation_level" />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="acknowledgment_rate" fill="#8884d8" name="Acknowledgment Rate (%)" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Overall Statistics
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Total Alerts"
                      secondary={escalationStats.timeout_statistics.total_alerts}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Escalation Rate"
                      secondary={`${escalationStats.timeout_statistics.escalation_rate}%`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Average Resolution Time"
                      secondary={
                        escalationStats.timeout_statistics.avg_resolution_time_minutes
                          ? `${Math.round(escalationStats.timeout_statistics.avg_resolution_time_minutes)} minutes`
                          : 'N/A'
                      }
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Escalation Level Details
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Level</TableCell>
                        <TableCell>Total Escalations</TableCell>
                        <TableCell>Acknowledged</TableCell>
                        <TableCell>Success Rate</TableCell>
                        <TableCell>Avg Response Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {escalationStats.escalation_statistics.map((stat) => (
                        <TableRow key={stat.escalation_level}>
                          <TableCell>Level {stat.escalation_level}</TableCell>
                          <TableCell>{stat.total_escalations}</TableCell>
                          <TableCell>{stat.acknowledged_count}</TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <LinearProgress
                                variant="determinate"
                                value={stat.acknowledgment_rate}
                                sx={{ width: 100 }}
                              />
                              <Typography variant="body2">
                                {stat.acknowledgment_rate}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {stat.avg_response_time_minutes
                              ? `${Math.round(stat.avg_response_time_minutes)}m`
                              : 'N/A'
                            }
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Caregiver Performance Tab */}
      {activeTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Caregiver Response Patterns
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Caregiver</TableCell>
                        <TableCell>Priority</TableCell>
                        <TableCell>Total Escalations</TableCell>
                        <TableCell>Acknowledgment Rate</TableCell>
                        <TableCell>Avg Response Time</TableCell>
                        <TableCell>Preferred Method</TableCell>
                        <TableCell>Contact Info</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {caregiverPatterns.map((pattern, index) => (
                        <TableRow key={index}>
                          <TableCell>{pattern.caregiver_name}</TableCell>
                          <TableCell>
                            <Chip
                              label={`Priority ${pattern.priority}`}
                              color={pattern.priority <= 2 ? 'error' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{pattern.total_escalations}</TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <LinearProgress
                                variant="determinate"
                                value={pattern.acknowledgment_rate}
                                sx={{ width: 100 }}
                              />
                              <Typography variant="body2">
                                {pattern.acknowledgment_rate}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {pattern.avg_response_time_minutes
                              ? `${Math.round(pattern.avg_response_time_minutes)}m`
                              : 'N/A'
                            }
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={pattern.preferred_method || 'N/A'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={1}>
                              {pattern.caregiver_phone && (
                                <Tooltip title={pattern.caregiver_phone}>
                                  <PhoneIcon fontSize="small" />
                                </Tooltip>
                              )}
                              {pattern.caregiver_email && (
                                <Tooltip title={pattern.caregiver_email}>
                                  <EmailIcon fontSize="small" />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Acknowledge Dialog */}
      <Dialog open={acknowledgeDialog.open} onClose={() => setAcknowledgeDialog({ open: false, alert: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Acknowledge Alert</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Acknowledged By"
            value={acknowledgeForm.acknowledged_by}
            onChange={(e) => setAcknowledgeForm({ ...acknowledgeForm, acknowledged_by: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Acknowledgment Notes"
            value={acknowledgeForm.acknowledgment_notes}
            onChange={(e) => setAcknowledgeForm({ ...acknowledgeForm, acknowledgment_notes: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
          <TextField
            fullWidth
            label="Estimated Resolution Time (minutes)"
            value={acknowledgeForm.estimated_resolution_time}
            onChange={(e) => setAcknowledgeForm({ ...acknowledgeForm, estimated_resolution_time: e.target.value })}
            margin="normal"
            type="number"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcknowledgeDialog({ open: false, alert: null })}>Cancel</Button>
          <Button onClick={handleAcknowledgeAlert} variant="contained" disabled={!acknowledgeForm.acknowledged_by}>
            Acknowledge
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resolve Dialog */}
      <Dialog open={resolveDialog.open} onClose={() => setResolveDialog({ open: false, alert: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Resolve Alert</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Resolved By"
            value={resolveForm.resolved_by}
            onChange={(e) => setResolveForm({ ...resolveForm, resolved_by: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Resolution Notes"
            value={resolveForm.resolution_notes}
            onChange={(e) => setResolveForm({ ...resolveForm, resolution_notes: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
          <TextField
            fullWidth
            label="Resolution Method"
            value={resolveForm.resolution_method}
            onChange={(e) => setResolveForm({ ...resolveForm, resolution_method: e.target.value })}
            margin="normal"
            placeholder="e.g., phone_call, in_person, remote_assistance"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialog({ open: false, alert: null })}>Cancel</Button>
          <Button onClick={handleResolveAlert} variant="contained" disabled={!resolveForm.resolved_by}>
            Resolve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tracking Dialog */}
      <Dialog open={trackingDialog.open} onClose={() => setTrackingDialog({ open: false, tracking: null })} maxWidth="md" fullWidth>
        <DialogTitle>Acknowledgment Tracking</DialogTitle>
        <DialogContent>
          {trackingDialog.tracking && (
            <Box>
              <Typography variant="h6" gutterBottom>Alert Information</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Alert ID:</strong> {trackingDialog.tracking.alert_id}</Typography>
                  <Typography variant="body2"><strong>Status:</strong> {trackingDialog.tracking.status}</Typography>
                  <Typography variant="body2"><strong>Escalation:</strong> {trackingDialog.tracking.escalation_count}/{trackingDialog.tracking.max_escalations}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Acknowledged By:</strong> {trackingDialog.tracking.acknowledged_by || 'N/A'}</Typography>
                  <Typography variant="body2"><strong>Acknowledged At:</strong> {trackingDialog.tracking.acknowledged_at ? formatDateTime(trackingDialog.tracking.acknowledged_at) : 'N/A'}</Typography>
                  <Typography variant="body2"><strong>Resolved At:</strong> {trackingDialog.tracking.resolved_at ? formatDateTime(trackingDialog.tracking.resolved_at) : 'N/A'}</Typography>
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom>Time Metrics</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Time to Acknowledge:</strong> {trackingDialog.tracking.time_metrics.time_to_acknowledge_minutes ? `${Math.round(trackingDialog.tracking.time_metrics.time_to_acknowledge_minutes)} minutes` : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Time to Resolve:</strong> {trackingDialog.tracking.time_metrics.time_to_resolve_minutes ? `${Math.round(trackingDialog.tracking.time_metrics.time_to_resolve_minutes)} minutes` : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom>Escalation History</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Level</TableCell>
                      <TableCell>Escalated To</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Sent At</TableCell>
                      <TableCell>Acknowledged</TableCell>
                      <TableCell>Acknowledged By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trackingDialog.tracking.escalation_logs.map((log, index) => (
                      <TableRow key={index}>
                        <TableCell>Level {log.escalation_level}</TableCell>
                        <TableCell>{log.escalated_to}</TableCell>
                        <TableCell>{log.escalation_method}</TableCell>
                        <TableCell>{formatDateTime(log.sent_at)}</TableCell>
                        <TableCell>
                          <Chip
                            label={log.acknowledged ? 'Yes' : 'No'}
                            color={log.acknowledged ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{log.acknowledged_by || 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrackingDialog({ open: false, tracking: null })}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdvancedAlertsDashboard;
